import Entities from './entities';
import EventTypes from './event-types';
import RuleActionModel from '@/common/models/rules/actions/rule-action-model';
import {specialFields} from './special/special-fields';
import SubscriptionInvoice from './email-templates/subscription-invoice';
import SubscriptionSummary from './email-templates/subscription-summary';

const eventSpecs = {
    [EventTypes.customerOneTimePasswordRequested]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
        ],
        links: [
            Entities.customer,
        ],
        extraPlaceholders: [
            {
                group: 'EventSpecific',
                placeholders: ['oneTimePassword'],
            },
        ],
    },
    [EventTypes.disputeCreated]: {
        description: null,
        actions: [
            RuleActionModel.Names.blacklist,
            RuleActionModel.Names.stopSubscriptions,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.dispute,
        ],
    },
    [EventTypes.gatewayAccountRequested]: {
        description: null,
        actions: [
            RuleActionModel.Names.pickGatewayAccount,
            RuleActionModel.Names.requestKyc,
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.showDescriptorDisclaimer,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.displayOtherChoices,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.transaction,
        ],
    },
    [EventTypes.invoicePaid]: {
        description: 'When an invoice is paid, you may want to send an email or trigger a webhook.',
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.invoice,
        ],
    },
    [EventTypes.invoiceIssued]: {
        description: 'When an invoice is issued, you may want to send an email or trigger a webhook.',
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
            RuleActionModel.Names.scheduleReminder,
        ],
        links: [
            Entities.invoice,
        ],
    },
    [EventTypes.invoicePastDue]: {
        description: 'When an invoice is past due, you may want to send an email.',
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.triggerWebhook,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.scheduleReminder,
            RuleActionModel.Names.resetReminder,
            RuleActionModel.Names.removeReminder,
        ],
        links: [
            Entities.invoice,
        ],
    },
    [EventTypes.nsfResponseReceived]: {
        description: 'When a non-sufficient funds response is received, you may want to add a fee or send an email.',
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.addNsfFee,
        ],
        links: [
            Entities.transaction,
        ],
    },
    [EventTypes.paymentCardExpired]: {
        description: null,
        actions: [
            RuleActionModel.Names.guessPaymentCardExpiration,
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
            RuleActionModel.Names.scheduleReminder,
            RuleActionModel.Names.resetReminder,
            RuleActionModel.Names.removeReminder,
        ],
        links: [
            Entities.paymentCard,
        ],
    },
    [EventTypes.riskScoreChanged]: {
        description: null,
        actions: [
            RuleActionModel.Names.blacklist,
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.transaction,
        ],
    },
    [EventTypes.subscriptionActivated]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
            RuleActionModel.Names.scheduleReminder,
        ],
        links: [
            Entities.subscription,
        ],
    },
    [EventTypes.subscriptionCanceled]: {
        description: null,
        actions: [
            RuleActionModel.Names.cancelScheduledPayments,
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.subscription,
        ],
    },
    [EventTypes.subscriptionReactivated]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.subscription,
        ],
    },
    [EventTypes.subscriptionCreated]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.subscription,
        ],
    },
    [EventTypes.subscriptionRenewed]: {
        description: null,
        actions: [
            RuleActionModel.Names.schedulePayment,
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
            RuleActionModel.Names.scheduleReminder,
            RuleActionModel.Names.removeReminder,
        ],
        links: [
            Entities.subscription,
            Entities.invoice,
        ],
    },
    [EventTypes.renewalInvoiceIssued]: {
        description: null,
        actions: [
            RuleActionModel.Names.schedulePayment,
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
            RuleActionModel.Names.scheduleReminder,
            RuleActionModel.Names.removeReminder,
        ],
        links: [
            Entities.subscription,
            Entities.invoice,
        ],
    },
    [EventTypes.sanctionsListMatched]: {
        description: 'When a customer\'s names found in sanctions list, you may want to send an email or trigger a webhook.',
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.customer,
        ],
    },
    [EventTypes.transactionAmountDiscrepancyFound]: {
        description: 'When a transaction amount discrepancy is found, you may want to send an email or trigger a webhook. ',
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.transaction,
        ],
    },
    [EventTypes.transactionDeclined]: {
        description: 'When a transaction is declined, you may want to retry again with different values.',
        actions: [
            RuleActionModel.Names.schedulePaymentRetry,
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.transaction,
        ],
    },
    [EventTypes.transactionDiscrepancyFound]: {
        description: 'When a transaction discrepancy is found, you may want to send an email or trigger a webhook.',
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.transaction,
        ],
    },
    [EventTypes.transactionProcessed]: {
        description: null,
        actions: [
            RuleActionModel.Names.addRiskScore,
            RuleActionModel.Names.blacklist,
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.stopSubscriptions,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.transaction,
        ],
    },
    [EventTypes.transactionProcessRequested]: {
        description: null,
        actions: [
            RuleActionModel.Names.addRiskScore,
            RuleActionModel.Names.offerPurchaseBump,
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.transaction,
        ],
    },
    [EventTypes.renewalInvoicePaymentDeclined]: {
        description: 'When a renewal invoice payment is declined, you may want to retry again.',
        actions: [
            RuleActionModel.Names.scheduleInvoiceRetry,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.invoice,
            Entities.transaction,
        ],
    },
    [EventTypes.renewalInvoicePaymentCanceled]: {
        description: 'When a renewal invoice payment is canceled.',
        actions: [
            RuleActionModel.Names.churnSubscription,
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.invoice,
            Entities.transaction,
        ],
    },
    [EventTypes.subscriptionEnded]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.subscription,
        ],
    },
    [EventTypes.subscriptionTrialEnded]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.tagOrUntagCustomer,
            RuleActionModel.Names.triggerWebhook,
            RuleActionModel.Names.scheduleReminder,
            RuleActionModel.Names.removeReminder,
        ],
        links: [
            Entities.subscription,
        ],
    },
    [EventTypes.subscriptionTrialEndReminder]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.triggerWebhook,
            RuleActionModel.Names.scheduleReminder,
            RuleActionModel.Names.removeReminder,
        ],
        links: [
            Entities.subscription,
        ],
    },
    [EventTypes.subscriptionRenewalReminder]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.triggerWebhook,
            RuleActionModel.Names.scheduleReminder,
            RuleActionModel.Names.removeReminder,
        ],
        links: [
            Entities.subscription,
        ],
    },
    [EventTypes.subscriptionRenewalReminder]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.triggerWebhook,
            RuleActionModel.Names.scheduleReminder,
            RuleActionModel.Names.removeReminder,
        ],
        links: [
            Entities.subscription,
        ],
    },
    [EventTypes.paymentCardExpirationReminder]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.triggerWebhook,
            RuleActionModel.Names.scheduleReminder,
            RuleActionModel.Names.resetReminder,
            RuleActionModel.Names.removeReminder,
        ],
        links: [
            Entities.paymentCard,
        ],
    },
    [EventTypes.invoicePastDueReminder]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.triggerWebhook,
            RuleActionModel.Names.scheduleReminder,
            RuleActionModel.Names.resetReminder,
            RuleActionModel.Names.removeReminder,
        ],
        links: [
            Entities.invoice,
        ],
    },
    [EventTypes.subscriptionTrialConverted]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.triggerWebhook,
            RuleActionModel.Names.scheduleReminder,
            RuleActionModel.Names.removeReminder,
        ],
        links: [
            Entities.subscription,
        ],
    },
    [EventTypes.paymentCardCreated]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.triggerWebhook,
            RuleActionModel.Names.scheduleReminder,
        ],
        links: [
            Entities.paymentCard,
        ],
    },
    [EventTypes.kycDocumentAccepted]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.kycDocument,
        ],
    },
    [EventTypes.kycDocumentRejected]: {
        description: null,
        actions: [
            RuleActionModel.Names.sendEmail,
            RuleActionModel.Names.triggerWebhook,
        ],
        links: [
            Entities.kycDocument,
        ],
    },
};

const entitiesSpecs = {
    [Entities.invoice]: {
        path: '/invoice',
        links: [
            Entities.customer,
            Entities.website,
            Entities.leadSource,
        ],
        attachableInvoiceId: '/id',
        autoCompleteFields: [
            {
                precursor: '/items/productId',
                entity: Entities.product,
                pick: 'id',
            },
            {
                field: 'customerId',
                entity: Entities.customer,
                pick: 'id',
            },
            {
                field: 'websiteId',
                entity: Entities.website,
                pick: 'id',
            },
            {
                precursor: '/transactions/gatewayAccountId',
                field: 'gatewayAccountId',
                entity: Entities.gatewayAccount,
                pick: 'id',
            },
            {
                precursor: '/transactions/customerId',
                field: 'customerId',
                entity: Entities.customer,
                pick: 'id',
            },
            {
                precursor: '/transactions/planIds',
                field: 'planIds',
                entity: Entities.plan,
                pick: 'id',
            },
            {
                precursor: '/transactions/websiteId',
                field: 'websiteId',
                entity: Entities.website,
                pick: 'id',
            },
            {
                precursor: '/transactions/subscriptionIds/',
                field: 'subscriptionIds',
                entity: Entities.subscription,
                pick: 'id',
            },
        ],
    },
    [Entities.subscription]: {
        path: '/subscription',
        links: [
            Entities.customer,
            Entities.website,
            Entities.plan,
            Entities.leadSource,
        ],
        attachableInvoiceId: '/recentInvoiceId',
    },
    [Entities.dispute]: {
        path: '/dispute',
        links: [
            Entities.customer,
            Entities.transaction,
        ],
    },
    [Entities.transaction]: {
        path: '/transaction',
        exclusions: ['bin'],
        links: [
            Entities.customer,
            Entities.website,
            Entities.paymentCard,
            Entities.bankAccount,
            Entities.leadSource,
        ],
        extraLinks: [
            {
                entity: Entities.invoice,
                type: Array,
                path: '/invoices',
            },
        ],
        autoCompleteFields: [
            {
                field: 'gatewayAccountId',
                entity: Entities.gatewayAccount,
                pick: 'id',
            },
            {
                field: 'customerId',
                entity: Entities.customer,
                pick: 'id',
            },
            {
                field: 'planIds',
                entity: Entities.plan,
                pick: 'id',
            },
            {
                field: 'websiteId',
                entity: Entities.website,
                pick: 'id',
            },
            {
                field: 'subscriptionIds',
                entity: Entities.subscription,
                pick: 'id',
            },
        ],
    },
    [Entities.customer]: {
        path: '/customer',
        links: [
            Entities.website,
        ],
        customerEmail: '/primaryAddress/emails/0/value',
        autoCompleteFields: [
            {
                field: 'id',
            },
            {
                precursor: '/tags/name',
                pick: 'name',
                entity: Entities.tag,
            },
        ],
    },
    [Entities.website]: {
        path: '/website',
        merchantEmail: '/serviceEmail',
        autoCompleteFields: [
            {
                field: 'id',
            },
        ],
    },
    [Entities.paymentCard]: {
        path: '/paymentCard',
        links: [
            Entities.customer,
        ],
        autoCompleteFields: [
            {
                field: 'customerId',
                entity: Entities.customer,
                pick: 'id',
            },
        ],
    },
    [Entities.bankAccount]: {
        path: '/bankAccount',
        links: [
            Entities.customer,
        ],
        autoCompleteFields: [
            {
                field: 'customerId',
                entity: Entities.customer,
                pick: 'id',
            },
        ],
    },
    [Entities.plan]: {
        path: '/plan',
    },
    [Entities.leadSource]: {
        path: '/leadSource',
    },
    [Entities.kycDocument]: {
        path: '/kycDocument',
        links: [
            Entities.customer,
        ],
        autoCompleteFields: [
            {
                field: 'customerId',
                entity: Entities.customer,
                pick: 'id',
            },
        ],
    },
};

const emailTemplatesSpecs = [
    {
        label: 'Subscription Initial Invoice',
        content: SubscriptionInvoice,
        isAvailable: eventType => [
            EventTypes.subscriptionCreated,
            EventTypes.subscriptionActivated,
        ].includes(eventType),
    },
    {
        label: 'Subscription Summary',
        content: SubscriptionSummary,
        isAvailable: eventType => [
            EventTypes.subscriptionActivated,
        ].includes(eventType),
    },
];

function generatePlaceholder(path) {
    return path.replace(/^\//g, '').replace(/\//g, '.');
}

function generatePathways({
    resource, base = '', parents = [], spec,
}) {
    const result = [];
    const path = `${base}${spec[resource].path}`;
    const placeholder = generatePlaceholder(path);
    const exclusions = spec[resource].exclusions || [];
    const customerEmail = spec[resource].customerEmail || null;
    const merchantEmail = spec[resource].merchantEmail || null;
    const attachableInvoiceId = spec[resource].attachableInvoiceId || null;
    const autoCompleteFields = spec[resource].autoCompleteFields || null;

    result.push({
        path,
        placeholder,
        resource,
        exclusions,
        merchantEmail,
        customerEmail,
        attachableInvoiceId,
        autoCompleteFields,
    });

    if (spec[resource].links) {
        spec[resource].links.forEach((link) => {
            if (!parents.includes(link)) {
                result.push(...generatePathways({
                    resource: link,
                    base: path,
                    parents: spec[resource].links,
                    spec,
                }));
            }
        });
    }
    if (spec[resource].extraLinks && !parents.length) {
        spec[resource].extraLinks.forEach(({entity, type, path: subPath}) => {
            if (!parents.includes(entity)) {
                const combinedPath = `${path}${subPath}`;
                result.push({
                    path: combinedPath,
                    type,
                    placeholder: generatePlaceholder(combinedPath),
                    resource: entity,
                    exclusions: [],
                    merchantEmail: null,
                    customerEmail: null,
                    attachableInvoiceId: null,
                    autoCompleteFields: spec[entity].autoCompleteFields || null,
                });
            }
        });
    }

    return result;
}

/**
 * For each group of extra placeholders merge into existing data or create
 * a new page. The `extraPlaceholders` property can be used for indexing where needed.
 * @param {array} extraGroups
 * @param {{}} accumulator
 */
function addExtraPlaceholders(extraGroups = [], accumulator) {
    extraGroups.forEach(({group, placeholders: extraPlaceholders}) => {
        const existingGroup = accumulator.find(g => g.group === group);
        if (!existingGroup) {
            accumulator.push({group, pathways: [], extraPlaceholders});
        } else {
            existingGroup.extraPlaceholders = extraPlaceholders;
        }
    });
}

export function generateSpecs(definitions, entities) {
    Object.keys(definitions)
        .forEach((eventType) => {
            const pathways = [];
            const placeholderGroups = [];
            const defaultEmailSenders = [];
            const defaultEmailReceivers = [];
            const attachableInvoiceIds = [];
            const emailTemplates = [];

            definitions[eventType].links.forEach((entity) => {
                const groupPathways = generatePathways({resource: entity, spec: entities});
                pathways.push(
                    ...groupPathways.map(({
                        path, resource, exclusions, type = null, autoCompleteFields = null,
                    }) => ({
                        path,
                        resource,
                        exclusions,
                        type,
                        autoCompleteFields,
                    })),
                );
                placeholderGroups.push({
                    group: entity,
                    pathways: groupPathways.map(({placeholder: path, resource}) => ({
                        path,
                        resource,
                    })),
                    extraPlaceholders: [],
                });
                // inject additional arbitrary placeholders not found in the spec
                if (definitions[eventType].extraPlaceholders) {
                    addExtraPlaceholders(
                        definitions[eventType].extraPlaceholders,
                        placeholderGroups,
                    );
                }

                groupPathways.forEach(({
                    resource,
                    placeholder,
                    merchantEmail,
                    customerEmail,
                    attachableInvoiceId,
                }) => {
                    if (merchantEmail && !defaultEmailSenders[resource]) {
                        defaultEmailSenders[resource] = `{{ ${placeholder}.${generatePlaceholder(merchantEmail)} }}`;
                    }
                    if (customerEmail && !defaultEmailReceivers[resource]) {
                        defaultEmailReceivers[resource] = `{{ ${placeholder}.${generatePlaceholder(customerEmail)} }}`;
                    }
                    if (attachableInvoiceId && !attachableInvoiceIds[resource]) {
                        attachableInvoiceIds[resource] = `{{ ${placeholder}.${generatePlaceholder(attachableInvoiceId)} }}`;
                    }
                });
            });

            // Inject special cases, like `random` criteria.
            definitions[eventType].pathways = [...pathways, ...specialFields];
            definitions[eventType].placeholderGroups = placeholderGroups;
            definitions[eventType].defaultEmailSenders = Object.values(defaultEmailSenders);
            definitions[eventType].defaultEmailReceivers = Object.values(defaultEmailReceivers);
            // TODO: Consider to add multiple attachable invoice to email form.
            // TODO: Consider to add more attachable files.
            definitions[eventType]
                .attachableInvoiceId = Object.values(attachableInvoiceIds)[0] || null;

            emailTemplatesSpecs.forEach((emailTemplate) => {
                if (emailTemplate.isAvailable(eventType, definitions[eventType])) {
                    emailTemplates.push(emailTemplate);
                }
            });
            definitions[eventType].emailTemplates = emailTemplates;
        });
    return definitions;
}

export default generateSpecs(eventSpecs, entitiesSpecs);
